// apps/web/src/pages/verify/[token].tsx — Phase 7 plan 07-06 (B2 fix consumed).
//
// Magic-link landing. The page reads `token` from `useParams()`,
// calls `Mutation.verifySignup`, and on success the server returns
// the new Restaurant + AdminUser and sets the `onboarding_access`
// cookie (Domain=.<env.BASE_DOMAIN> — the cookie auto-flows across
// subdomains). The page then redirects to
// `<slug>.<baseDomain>/onboarding/<onboardingToken>/address`.
//
// In dev path-based mode the slug is in the URL prefix
// (`/t/<slug>/verify/<token>`); in production the verify page may
// be reached via the bare-domain /verify/<token> path (which
// looks up the slug from the response). The redirect target
// preserves the dev path prefix when present.

import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ClientError } from "graphql-request";
import { gqlRequest } from "../../lib/api";
import { devModeBanner, resolveTenantSlug } from "../../lib/tenant-resolver";

const VERIFY_SIGNUP_MUTATION = /* GraphQL */ `
  mutation VerifySignup($token: String!) {
    verifySignup(token: $token) {
      accessToken
      expiresIn
      user {
        id
        email
        role
      }
      restaurant {
        id
        slug
      }
    }
  }
`;

interface VerifySignupResponse {
  verifySignup: {
    accessToken: string;
    expiresIn: number;
    user: { id: string; email: string; role: string };
    restaurant: { id: string; slug: string };
  };
}

export function VerifyTokenPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();

  const mutation = useMutation({
    mutationFn: () =>
      gqlRequest<VerifySignupResponse>(VERIFY_SIGNUP_MUTATION, {
        token,
      }),
    onSuccess: (res) => {
      const slug = res.verifySignup.restaurant.slug;
      if (typeof window === "undefined") return;
      const pathPrefixSlug = resolveTenantSlug(
        window.location.hostname,
        window.location.pathname,
      );
      // Dev path-based mode preserves the /t/<slug>/ prefix so the
      // onboarding wizard also runs in dev mode. In prod we hard-
      // navigate to the tenant subdomain (B2 fix: the onboarding
      // cookie auto-flows because it carries Domain=.<baseDomain>).
      let target: string;
      if (pathPrefixSlug) {
        const remainder = window.location.pathname
          .replace(/^\/t\/[a-z0-9-]{3,40}/, "")
          .replace(/^\/verify\/[^/]+$/, "");
        target = `${window.location.origin}/t/${slug}/onboarding/${res.verifySignup.accessToken}/address${remainder}`;
      } else {
        const baseDomain = window.location.host.replace(/^[^.]+\./, "");
        target = `${window.location.protocol}//${slug}.${baseDomain}/onboarding/${res.verifySignup.accessToken}/address`;
      }
      window.location.assign(target);
    },
  });

  useEffect(() => {
    if (mutation.isIdle) {
      mutation.mutate();
    }
    // mutation is intentionally not in deps — we only fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (mutation.isError) {
    const err = mutation.error;
    if (err instanceof ClientError) {
      const codeRaw = err.response?.errors?.[0]?.extensions?.code;
      const code = typeof codeRaw === "string" ? codeRaw : null;
      return (
        <main
          className="min-h-screen bg-bg text-text font-body flex items-center justify-center px-6 py-12"
          data-testid="verify-invalid"
        >
          <div
            className="card"
            style={{
              borderRadius: 14,
              width: "min(440px, 100%)",
              padding: "28px 24px",
            }}
          >
            <h1 className="font-heading text-[22px] font-semibold mb-2">
              Link nicht mehr gültig
            </h1>
            <p className="text-sm text-text/70">
              Dieser Bestätigungslink ist abgelaufen oder wurde bereits
              verwendet. Bitte starten Sie die Registrierung erneut.
            </p>
            {code ? (
              <p className="text-[11px] text-text/50 mt-3">
                Fehlercode: {code}
              </p>
            ) : null}
          </div>
        </main>
      );
    }
  }

  const slug = resolveTenantSlug(
    typeof window !== "undefined" ? window.location.hostname : "",
    typeof window !== "undefined" ? window.location.pathname : "",
  );
  const showDevBanner = typeof window !== "undefined" && import.meta.env.DEV;

  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex flex-col items-center justify-center px-6 py-12"
      data-testid="verify-page"
    >
      {showDevBanner ? (
        <p className="text-[11px] text-text/60 mb-3" data-testid="dev-banner">
          {devModeBanner(slug)}
        </p>
      ) : null}
      <div
        className="card"
        style={{
          borderRadius: 14,
          width: "min(440px, 100%)",
          padding: "28px 24px",
        }}
      >
        <h1 className="font-heading text-[22px] font-semibold mb-2">
          Bestätigung läuft…
        </h1>
        <p className="text-sm text-text/70">
          Wir verifizieren Ihre E-Mail-Adresse. Sie werden gleich zum Onboarding
          weitergeleitet.
        </p>
      </div>
    </main>
  );
}
